import { defineQuery } from 'bitecs';
import { Position, Animal, Plant, animalQuery, plantQuery } from '../world.js';
import { TILE_SIZE, CHUNK_SIZE, RENDER_RADIUS } from '../config.js';

/**
 * Performance optimization system that manages entity processing based on distance
 * and implements Level of Detail (LOD) for better performance
 */
export class PerformanceOptimizer {
    constructor() {
        this.activeRadius = RENDER_RADIUS * 2; // Process entities within 2x render radius
        this.chunkSize = CHUNK_SIZE * TILE_SIZE;
        this.maxEntitiesPerFrame = 50;
        this.processedEntities = new Set();
        this.frameCount = 0;
        
        // Performance metrics
        this.metrics = {
            entitiesProcessed: 0,
            entitiesSkipped: 0,
            averageProcessingTime: 0,
            frameTime: 0
        };
    }
    
    /**
     * Get entities within active radius of player
     */
    getActiveEntities(world, playerX, playerY) {
        const animals = animalQuery(world);
        const plants = plantQuery(world);
        
        const activeAnimals = this.filterEntitiesByDistance(animals, playerX, playerY);
        const activePlants = this.filterEntitiesByDistance(plants, playerX, playerY);
        
        return {
            animals: activeAnimals,
            plants: activePlants,
            total: activeAnimals.length + activePlants.length
        };
    }
    
    /**
     * Filter entities based on distance from player
     */
    filterEntitiesByDistance(entities, playerX, playerY) {
        return entities.filter(entity => {
            const x = Animal.x[entity] || Plant.x[entity] || 0;
            const y = Animal.y[entity] || Plant.y[entity] || 0;
            
            const distance = Math.sqrt(Math.pow(x - playerX, 2) + Math.pow(y - playerY, 2));
            return distance <= this.activeRadius * TILE_SIZE;
        });
    }
    
    /**
     * Get LOD level based on distance
     */
    getLODLevel(distance) {
        const chunkDistance = distance / this.chunkSize;
        
        if (chunkDistance <= 3) return 'HIGH';      // Full detail
        if (chunkDistance <= 6) return 'MEDIUM';    // Reduced detail
        if (chunkDistance <= 10) return 'LOW';      // Minimal detail
        return 'NONE';                               // Don't process
    }
    
    /**
     * Process entities with LOD optimization
     */
    processEntitiesWithLOD(world, entities, processor, playerX, playerY) {
        const startTime = performance.now();
        
        // Sort entities by distance for priority processing
        entities.sort((a, b) => {
            const distA = this.getEntityDistance(a, playerX, playerY);
            const distB = this.getEntityDistance(b, playerX, playerY);
            return distA - distB;
        });
        
        let processedCount = 0;
        const toProcess = entities.slice(0, this.maxEntitiesPerFrame);
        
        for (const entity of toProcess) {
            const distance = this.getEntityDistance(entity, playerX, playerY);
            const lod = this.getLODLevel(distance);
            
            if (lod === 'NONE') continue;
            
            // Process entity based on LOD level
            switch (lod) {
                case 'HIGH':
                    processor(entity, 'HIGH');
                    break;
                case 'MEDIUM':
                    processor(entity, 'MEDIUM');
                    break;
                case 'LOW':
                    processor(entity, 'LOW');
                    break;
            }
            
            processedCount++;
        }
        
        // Update metrics
        const processingTime = performance.now() - startTime;
        this.metrics.entitiesProcessed = processedCount;
        this.metrics.entitiesSkipped = entities.length - processedCount;
        this.metrics.averageProcessingTime = processingTime / processedCount || 0;
        
        return processedCount;
    }
    
    /**
     * Get distance of entity from position
     */
    getEntityDistance(entity, x, y) {
        const entityX = Animal.x[entity] || Plant.x[entity] || 0;
        const entityY = Animal.y[entity] || Plant.y[entity] || 0;
        return Math.sqrt(Math.pow(entityX - x, 2) + Math.pow(entityY - y, 2));
    }
    
    /**
     * Adaptive quality adjustment based on performance
     */
    adjustQuality(frameTime) {
        this.metrics.frameTime = frameTime;
        
        if (frameTime > 16.67) { // Below 60 FPS
            // Reduce processing load
            this.maxEntitiesPerFrame = Math.max(20, this.maxEntitiesPerFrame - 5);
            this.activeRadius = Math.max(RENDER_RADIUS, this.activeRadius - 1);
        } else if (frameTime < 8.33) { // Above 120 FPS
            // Increase processing load
            this.maxEntitiesPerFrame = Math.min(100, this.maxEntitiesPerFrame + 5);
            this.activeRadius = Math.min(RENDER_RADIUS * 3, this.activeRadius + 1);
        }
    }
    
    /**
     * Get performance metrics
     */
    getMetrics() {
        return { ...this.metrics };
    }
    
    /**
     * Reset metrics for new measurement cycle
     */
    resetMetrics() {
        this.metrics.entitiesProcessed = 0;
        this.metrics.entitiesSkipped = 0;
        this.frameCount++;
    }
}

/**
 * Chunk-based culling system for better performance
 */
export class ChunkCullingSystem {
    constructor() {
        this.chunkSize = CHUNK_SIZE * TILE_SIZE;
        this.loadedChunks = new Map();
        this.visibleChunks = new Set();
        this.chunkCache = new Map();
    }
    
    /**
     * Get chunk key for position
     */
    getChunkKey(x, y) {
        const chunkX = Math.floor(x / this.chunkSize);
        const chunkY = Math.floor(y / this.chunkSize);
        return `${chunkX},${chunkY}`;
    }
    
    /**
     * Get visible chunks based on player position
     */
    getVisibleChunks(playerX, playerY, renderRadius) {
        const playerChunkX = Math.floor(playerX / this.chunkSize);
        const playerChunkY = Math.floor(playerY / this.chunkSize);
        const chunkRadius = Math.ceil(renderRadius * TILE_SIZE / this.chunkSize);
        
        const visible = new Set();
        
        for (let dx = -chunkRadius; dx <= chunkRadius; dx++) {
            for (let dy = -chunkRadius; dy <= chunkRadius; dy++) {
                const chunkKey = `${playerChunkX + dx},${playerChunkY + dy}`;
                visible.add(chunkKey);
            }
        }
        
        return visible;
    }
    
    /**
     * Update visible chunks and load/unload as needed
     */
    updateVisibleChunks(playerX, playerY, renderRadius) {
        const newVisibleChunks = this.getVisibleChunks(playerX, playerY, renderRadius);
        
        // Find chunks to unload
        for (const chunkKey of this.visibleChunks) {
            if (!newVisibleChunks.has(chunkKey)) {
                this.unloadChunk(chunkKey);
            }
        }
        
        // Find chunks to load
        for (const chunkKey of newVisibleChunks) {
            if (!this.visibleChunks.has(chunkKey)) {
                this.loadChunk(chunkKey);
            }
        }
        
        this.visibleChunks = newVisibleChunks;
    }
    
    /**
     * Load chunk data
     */
    loadChunk(chunkKey) {
        if (!this.chunkCache.has(chunkKey)) {
            // Initialize chunk data
            this.chunkCache.set(chunkKey, {
                entities: [],
                lastUpdate: Date.now(),
                loaded: true
            });
        }
        this.loadedChunks.set(chunkKey, this.chunkCache.get(chunkKey));
    }
    
    /**
     * Unload chunk data
     */
    unloadChunk(chunkKey) {
        this.loadedChunks.delete(chunkKey);
        // Keep in cache for potential reuse
    }
    
    /**
     * Check if entity is in visible chunk
     */
    isEntityVisible(entityX, entityY) {
        const chunkKey = this.getChunkKey(entityX, entityY);
        return this.visibleChunks.has(chunkKey);
    }
    
    /**
     * Get entities in visible chunks
     */
    getVisibleEntities(world) {
        const visibleEntities = [];
        
        for (const [chunkKey, chunk] of this.loadedChunks) {
            visibleEntities.push(...chunk.entities);
        }
        
        return visibleEntities;
    }
}

/**
 * Entity pool system for memory management
 */
export class EntityPool {
    constructor() {
        this.pools = new Map();
        this.maxPoolSize = 1000;
    }
    
    /**
     * Get entity from pool or create new one
     */
    getEntity(type) {
        if (!this.pools.has(type)) {
            this.pools.set(type, []);
        }
        
        const pool = this.pools.get(type);
        return pool.length > 0 ? pool.pop() : this.createEntity(type);
    }
    
    /**
     * Return entity to pool
     */
    returnEntity(type, entity) {
        if (!this.pools.has(type)) {
            this.pools.set(type, []);
        }
        
        const pool = this.pools.get(type);
        if (pool.length < this.maxPoolSize) {
            this.resetEntity(entity);
            pool.push(entity);
        }
    }
    
    /**
     * Create new entity
     */
    createEntity(type) {
        // This would integrate with your ECS system
        return { type, created: Date.now() };
    }
    
    /**
     * Reset entity state
     */
    resetEntity(entity) {
        // Reset entity to default state
        entity.active = false;
        entity.lastUsed = Date.now();
    }
    
    /**
     * Clean up old entities from pools
     */
    cleanup() {
        const now = Date.now();
        const maxAge = 60000; // 1 minute
        
        for (const [type, pool] of this.pools) {
            const filtered = pool.filter(entity => 
                now - entity.lastUsed < maxAge
            );
            this.pools.set(type, filtered);
        }
    }
}

// Export singleton instances
export const performanceOptimizer = new PerformanceOptimizer();
export const chunkCullingSystem = new ChunkCullingSystem();
export const entityPool = new EntityPool();
